// ShuffleCard — Bucket-3 stateful in-round UI for the Shuffle Card game.
//
// Two modes of engagement:
//   1. Compact chip strip inline on the scorecard (this component's
//      "compact" prop) — reflects the current draw + active effects.
//      Tap to open the full state view.
//   2. Full modal with history + deck status + active-effect list.
//
// Draw UX (locked 2026-07-14):
//   * When the scorekeeper reaches a new hole with picks_remaining > 0
//     for the caller's side, four face-down cards are offered.
//   * Tap a card → flip animation → effect text below.
//   * Save to shuffle_draw state; another pick fires only if
//     picks_remaining is still > 0 (Double Down = 2 picks; ♦5 compound
//     bumps picks_remaining by 2 client-side).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

async function callStandalone(action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const res = await fetch('/.netlify/functions/standalone-game', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

// ── Presentational: single card face ────────────────────────────────────
function CardFace({ card, small, large }) {
  const suit = card ? card.slice(-1) : ''
  const rank = card ? card.slice(0, -1) : ''
  const isRed = suit === '♦' || suit === '♥'
  // Three sizes: small (inline chip / history row), default (unused),
  // large (centered reveal card in DrawModal).
  const size = small
    ? { w: 42, h: 60, rank: '0.9rem', suit: '1rem', pad: '4px 6px', radius: 6 }
    : large
      ? { w: '100%', h: '100%', rank: '1.9rem', suit: '3rem', pad: '10px 12px', radius: 8 }
      : { w: 88, h: 124, rank: '2rem', suit: '2.4rem', pad: '4px 6px', radius: 6 }
  return (
    <div style={{
      width: size.w, height: size.h,
      borderRadius: size.radius, background: '#f8f6ee',
      color: isRed ? '#b62d3a' : '#1a1a1a',
      border: '1px solid #d5cfb8',
      boxShadow: large ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 6px rgba(0,0,0,0.35)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'space-between', padding: size.pad,
      fontWeight: 700,
    }}>
      <span style={{ alignSelf: 'flex-start', fontSize: size.rank, lineHeight: 1 }}>{rank}</span>
      <span style={{ fontSize: size.suit, lineHeight: 1 }}>{suit}</span>
      <span style={{ alignSelf: 'flex-end', fontSize: size.rank, lineHeight: 1, transform: 'rotate(180deg)' }}>{rank}</span>
    </div>
  )
}

function CardBack({ small, onClick }) {
  const size = small ? { w: 42, h: 60 } : { w: 88, h: 124 }
  return (
    <button
      onClick={onClick}
      aria-label="Draw this card"
      style={{
        width: size.w, height: size.h,
        borderRadius: 6, cursor: onClick ? 'pointer' : 'default',
        background: 'repeating-linear-gradient(45deg, #1a3a1a 0, #1a3a1a 8px, #274d27 8px, #274d27 16px)',
        border: '2px solid #d4b97a',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        padding: 0,
      }}
    />
  )
}

// ── Draw modal — 2x2 popup + reveal animation ─────────────────────────
// State machine (locked with Jacob 2026-07-14):
//   'offered' → user sees 4 face-down cards in a 2x2 grid; tap one
//   'revealing' → the 3 unpicked cards fade out; picked card zooms to
//                 center + flips (CSS rotateY 180deg)
//   'reading' → effect text banner slides up under the flipped card;
//               dwells for a beat so the group can read
//   'closing' → modal fades out; committed by an autoClose timeout
//               after ~3s of reading
// The parent onPick fires as soon as the card is picked (server call
// happens in parallel with the reveal); onAutoClose fires when the
// animation cycle completes so the parent can refresh state.
function DrawModal({ offer, picksRemaining, busy, revealCard, onPick, onAutoClose, onDismiss }) {
  // Phase is derived from revealCard (parent-owned). When the parent
  // sets revealCard on a pick, we advance offered → revealing → reading
  // → closing. When the parent clears revealCard, we snap back to
  // offered to accept the next pick (Double Down / ♦5 compound).
  const [phase, setPhase] = useState(revealCard ? 'reading' : 'offered')
  const [flipped, setFlipped] = useState(false)

  // Ref-mirror of onAutoClose so the reveal-sequence effect can call
  // the LATEST parent callback WITHOUT taking a fresh dependency each
  // parent render. Jacob 2026-07-14: the modal was flip-cycling for
  // 10-20s. Root cause was the effect deps included onAutoClose, which
  // was an inline arrow in the parent (new reference every render); the
  // ShuffleCard auto-refresh interval re-rendered the parent every 5s,
  // reset all timers, and restarted the reveal cycle indefinitely.
  const onAutoCloseRef = useRef(onAutoClose)
  useEffect(() => { onAutoCloseRef.current = onAutoClose }, [onAutoClose])

  // Parent set revealCard → run the reveal sequence.
  // First frame: flipped=false (face-down centered) so the browser
  // paints the "before" state. Next frame: flipped=true so the CSS
  // transition on transform kicks in reliably. Then dwell 3s in
  // reading, then close.
  useEffect(() => {
    if (!revealCard) {
      setPhase('offered')
      setFlipped(false)
      return
    }
    setPhase('revealing')
    setFlipped(false)
    // Two RAFs — first commits the initial transform, second flips.
    // Without this back-to-back rAF, some browsers coalesce the two
    // state changes into one paint and skip the transition entirely
    // (Jacob 2026-07-14: "no transitional flip — face-down then closed").
    let raf1 = requestAnimationFrame(() => {
      raf1 = requestAnimationFrame(() => setFlipped(true))
    })
    const toReading = setTimeout(() => setPhase('reading'), 800)
    const toClose = setTimeout(() => {
      setPhase('closing')
      onAutoCloseRef.current?.()
    }, 800 + 3000)
    return () => {
      cancelAnimationFrame(raf1)
      clearTimeout(toReading)
      clearTimeout(toClose)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealCard])

  const handlePick = (card) => {
    if (phase !== 'offered' || busy) return
    onPick(card) // parent will set revealCard which triggers the effect above
  }

  const eff = revealCard ? effectForCard(revealCard) : null

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0, 0, 0, 0.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
      transition: 'opacity 0.3s',
      opacity: phase === 'closing' ? 0 : 1,
    }} onClick={phase === 'offered' ? onDismiss : undefined}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 360, width: '100%',
          padding: 20,
          background: 'var(--green-deep)',
          border: '1px solid var(--gold)',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          position: 'relative',
          minHeight: 300,
        }}
      >
        {/* Header */}
        <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--gold)', textAlign: 'center' }}>
          {phase === 'offered'
            ? (picksRemaining === 1 ? 'Draw your Shuffle card' : `Draw ${picksRemaining} Shuffle cards`)
            : phase === 'revealing' ? 'Revealing…' : 'Your card'}
        </p>
        {phase === 'offered' && (
          <p style={{ margin: '4px 0 16px', fontSize: '0.75rem', color: 'var(--gray-300)', textAlign: 'center' }}>
            Tap a card to flip it.
          </p>
        )}

        {/* Offered state: 2x2 grid */}
        {phase === 'offered' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
            justifyItems: 'center',
          }}>
            {offer.slice(0, 4).map(card => (
              <CardBack
                key={card}
                onClick={busy ? null : () => handlePick(card)}
              />
            ))}
          </div>
        )}

        {/* Revealing + reading — centered flipping card + effect text */}
        {(phase === 'revealing' || phase === 'reading') && revealCard && (
          <div style={{
            marginTop: 16,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 14,
          }}>
            <FlippingCard card={revealCard} flipped={flipped} />
            <div style={{
              maxWidth: 300,
              padding: 12,
              background: 'var(--green-dark)',
              border: '1px solid var(--gold)',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: '0.85rem',
              fontWeight: 600, color: 'var(--cream)', lineHeight: 1.4,
              opacity: phase === 'reading' ? 1 : 0,
              transform: phase === 'reading' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.3s 0.1s, transform 0.3s 0.1s',
              minHeight: 40,
            }}>
              {eff?.text || revealCard}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Client-side effect catalog lookup — mirrors netlify/functions/_shuffle.js
// enough to display the rule text without a server round-trip. Server is
// still the source of truth for persistence; this is presentation-only.
function effectForCard(card) {
  if (!card) return null
  const suit = card.slice(-1)
  const rank = card.slice(0, -1)
  const clubLabel = rankClubLabel(rank)
  if (suit === '♠') return { text: `♠ ${rank}: ${clubLabel} removed for the ROUND.` }
  if (suit === '♣') return { text: `♣ ${rank}: ${clubLabel} removed for THIS hole only.` }
  if (suit === '♦') return { text: DIAMOND_TEXT[rank] || `♦ ${rank}` }
  if (suit === '♥') return { text: HEART_TEXT[rank] || `♥ ${rank}` }
  return null
}
function rankClubLabel(r) {
  if (r === 'A') return 'Driver'
  if (['2','3','4','5'].includes(r)) return 'Long club (fairway wood / hybrid / long iron)'
  if (['6','7','8','9'].includes(r)) return `${r}-iron`
  if (['10','J','Q'].includes(r)) return 'A wedge'
  if (r === 'K') return 'Putter'
  return 'A club'
}
const DIAMOND_TEXT = {
  '2': '♦ 2: Cannot use putter this hole.',
  '3': '♦ 3: Use only 1 club this hole (plus putter).',
  '4': '♦ 4: +1 stroke on your hole score.',
  '5': '♦ 5: Draw 2 more cards. Both stack.',
  '6': '♦ 6: Play from one tee-box back.',
  '7': '♦ 7: Opposing players choose your tee-off club.',
  '8': '♦ 8: Swap ball position once on the green.',
  '9': '♦ 9: Must use driver on the next par-3.',
  '10': '♦ 10: +1 stroke every time you talk this hole.',
  'J': '♦ J: +2 strokes on your hole score.',
  'Q': '♦ Q: Ladies play from your group’s Men’s tee.',
  'K': '♦ K: Men add +1 stroke this hole.',
  'A': '♦ A: Pick 3 clubs to use this hole.',
}
const HEART_TEXT = {
  '2': '♥ 2: −1 stroke for every player this hole.',
  '3': '♥ 3: Play this hole from the front tees.',
  '4': '♥ 4: Free mulligan on any one shot this hole.',
  '5': '♥ 5: Play from one tee-box forward.',
  '6': '♥ 6: Bank a gimme — cash in on any putt this round.',
  '7': '♥ 7: Return one active Spade to the deck.',
  '8': '♥ 8: All putts free this hole (once on the green).',
  '9': '♥ 9: Sand shots played from fairway center or green edge.',
  '10': '♥ 10: Return ALL active Spades to the deck.',
  'J': '♥ J: Return ALL active Clubs to the deck.',
  'Q': '♥ Q: Ladies get −2 strokes for next 3 holes.',
  'K': '♥ K: Men get −2 strokes for next 2 holes.',
  'A': '♥ A: Drop your worst hole from the round.',
}

// Card that starts face-down and flips to face-up. CSS 3D flip — front is
// the CardBack; back is the CardFace at rotateY(180deg).
function FlippingCard({ card, flipped }) {
  return (
    <div style={{
      width: 120, height: 168,
      perspective: 800,
    }}>
      <div style={{
        width: '100%', height: '100%',
        position: 'relative',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.7s',
        transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
        }}>
          <CardBackLarge />
        </div>
        <div style={{
          position: 'absolute', inset: 0,
          backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
        }}>
          <CardFace card={card} large />
        </div>
      </div>
    </div>
  )
}
function CardBackLarge() {
  return (
    <div style={{
      width: '100%', height: '100%',
      borderRadius: 8,
      background: 'repeating-linear-gradient(45deg, #1a3a1a 0, #1a3a1a 10px, #274d27 10px, #274d27 20px)',
      border: '3px solid #d4b97a',
      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    }} />
  )
}

// ── Effect banner — visible after a card was picked this hole ──────────
function EffectBanner({ card, effect, holeNumber }) {
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'center',
      padding: 10, borderRadius: 8,
      background: 'var(--green-deep)',
      border: '1px solid var(--green-mid)',
      marginBottom: 8,
    }}>
      <CardFace card={card} small />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Hole {holeNumber}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: '0.82rem', fontWeight: 600, color: 'var(--cream)' }}>
          {effect?.text || card}
        </p>
      </div>
    </div>
  )
}

// ── History view ───────────────────────────────────────────────────────
// Two filters, decided at the call site via `variant`:
//   inline (scorecard) → cards from holes STRICTLY BEFORE the
//                        scorekeeper's current hole; the current
//                        hole's card lives in the EffectBanner up top.
//   full   (/shuffle)  → EVERY drawn card EXCEPT the most-recent one,
//                        which is the "In Play" banner up top. Hole-
//                        agnostic per Jacob 2026-07-14.
function HistoryView({ draws, currentHole, variant }) {
  let past
  if (variant === 'full') {
    // Every draw sorted by hole desc, skipping the newest (that one
    // renders as "In Play" up top).
    const sorted = [...draws].sort(
      (a, b) => (b.hole_number - a.hole_number) || 0
    )
    past = sorted.slice(1)
  } else {
    past = draws.filter(d => d.hole_number < currentHole)
  }
  if (!past.length) return (
    <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', margin: 0 }}>No completed holes yet.</p>
  )
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {past.map((d, i) => {
        return (
          <div
            key={`${d.hole_number}-${i}-${d.card}`}
            style={{
              display: 'flex', gap: 8, alignItems: 'center',
              padding: 6, borderRadius: 6,
              background: 'var(--green-dark)',
              border: '1px solid var(--green-mid)',
              opacity: past ? 0.55 : 1,
            }}
          >
            <CardFace card={d.card} small />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>
                Hole {d.hole_number}{d.side_id && d.side_id !== 'group' ? ` · Side ${d.side_id}` : ''}
              </p>
              <p style={{
                margin: '2px 0 0', fontSize: '0.78rem',
                color: 'var(--cream)',
                textDecoration: past ? 'line-through' : 'none',
              }}>
                {d.effect?.text || d.card}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Active effects — spade removals + persistent Heart bonuses ─────────
function ActiveEffects({ activeSpades, crossHoleBonuses, currentHole }) {
  const liveSpades = activeSpades.filter(s => !s.cleared_at_hole || s.cleared_at_hole > currentHole)
  const liveHearts = crossHoleBonuses.filter(b => !b.expires_at_hole || b.expires_at_hole >= currentHole)
  if (liveSpades.length === 0 && liveHearts.length === 0) {
    return null
  }
  return (
    <div style={{ marginBottom: 8 }}>
      <p style={{ margin: '0 0 4px', fontSize: '0.7rem', color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        In play
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {liveSpades.map((s, i) => (
          <span key={`sp-${i}`} style={chipStyle('#1a1a1a', '#e8e8e8')}>
            ♠ {s.card} · no {clubShortLabel(s.category)}
          </span>
        ))}
        {liveHearts.map((h, i) => (
          <span key={`ht-${i}`} style={chipStyle('#2a1416', '#f88a92')}>
            ♥ {h.card}{h.expires_at_hole ? ` · thru H${h.expires_at_hole}` : ''}
          </span>
        ))}
      </div>
    </div>
  )
}

function clubShortLabel(cat) {
  switch (cat) {
    case 'driver': return 'driver'
    case 'putter': return 'putter'
    case 'wedge':  return 'wedge'
    case 'long':   return 'long club'
    case 'iron_6': return '6i'
    case 'iron_7': return '7i'
    case 'iron_8': return '8i'
    case 'iron_9': return '9i'
    default:       return cat || 'club'
  }
}

function chipStyle(bg, fg) {
  return {
    display: 'inline-flex', gap: 4, alignItems: 'center',
    padding: '3px 8px', borderRadius: 999,
    background: bg, color: fg,
    fontSize: '0.68rem', fontWeight: 600,
  }
}

// ── Etiquette gate ─────────────────────────────────────────────────────
function EtiquetteModal({ open, onAck }) {
  if (!open) return null
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1200,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        maxWidth: 380, background: 'var(--green-deep)',
        border: '1px solid var(--gold)', borderRadius: 10,
        padding: 20, textAlign: 'center',
      }}>
        <h3 style={{ margin: '0 0 8px', color: 'var(--gold)' }}>Shuffle — Reminder</h3>
        <p style={{ fontSize: '0.85rem', lineHeight: 1.5, color: 'var(--cream)' }}>
          Players are expected to maintain proper golf etiquette for pace
          of play and be respectful to other players, groups, and the
          course. TournyFlex is not responsible for your actions or
          hindrance to others or the course.
        </p>
        <button
          onClick={onAck}
          style={{
            marginTop: 12, padding: '10px 18px',
            background: 'var(--gold)', color: 'var(--green-deep)',
            border: 'none', borderRadius: 6,
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          I understand — let’s play
        </button>
      </div>
    </div>
  )
}

// ── Main ────────────────────────────────────────────────────────────────
export default function ShuffleCard({
  game_id,
  viewerPlayerId,
  viewerCurrentHole,
  currentHolePar,
  autoRefreshMs = 5000,
  // 'inline' (default) → compact banner + active-effects chip strip +
  // full-screen picker modal when picks_remaining > 0. Used on the
  // scorecard between scoring and group summary.
  // 'full' → same as inline PLUS the history view expanded by default,
  // deck count, mode. Used on /shuffle.
  variant = 'inline',
}) {
  const [state, setState] = useState(null)
  const [offer, setOffer] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [showHistory, setShowHistory] = useState(variant === 'full')
  // When user dismisses the picker modal by tapping outside, we hide
  // it until the next hole change so they can score the current hole
  // and come back later.
  const [pickerHidden, setPickerHidden] = useState(false)
  useEffect(() => { setPickerHidden(false) }, [viewerCurrentHole])
  // Reveal keeps the modal ALIVE through the animation cycle even after
  // the async server-side shuffle_pick completes and drops offer
  // picks_remaining to 0. Prior version relied on picks_remaining>0 for
  // the modal's mount condition — as soon as the pick was recorded, the
  // modal unmounted, killing the flip animation Jacob was expecting to
  // see. Now the parent explicitly clears `revealCard` when the modal's
  // onAutoClose fires (3s dwell after flip completes).
  const [revealCard, setRevealCard] = useState(null)

  const load = useCallback(async () => {
    if (!game_id) return
    try {
      const data = await callStandalone('shuffle_state', { game_id })
      setState(data)
      setErr(null)
    } catch (e) {
      setErr(e.message)
    }
  }, [game_id])

  const loadOffer = useCallback(async () => {
    if (!game_id || !viewerCurrentHole) return
    try {
      const data = await callStandalone('shuffle_offer', {
        game_id, hole_number: viewerCurrentHole,
      })
      setOffer(data)
    } catch (e) {
      // Non-fatal — offer is best-effort. If deck is exhausted the
      // server returns an empty offer.
      setOffer(null)
    }
  }, [game_id, viewerCurrentHole])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadOffer() }, [loadOffer])
  useEffect(() => {
    if (!autoRefreshMs) return
    const id = setInterval(() => { load() }, autoRefreshMs)
    return () => clearInterval(id)
  }, [load, autoRefreshMs])

  const ackEtiquette = async () => {
    try {
      await callStandalone('shuffle_ack_etiquette', { game_id })
      await load()
    } catch {}
  }

  const onPick = async (card) => {
    // Set revealCard synchronously so the modal's mount condition holds
    // through the whole reveal cycle, even after the fetch resolves and
    // offer.picks_remaining drops to 0.
    setRevealCard(card)
    setBusy(true)
    try {
      await callStandalone('shuffle_pick', {
        game_id, hole_number: viewerCurrentHole,
        card, hole_par: currentHolePar,
      })
      await loadOffer()
      await load()
    } catch (e) {
      setErr(e.message)
      // Clear the reveal so a rejected pick doesn't leave a stale card
      // frozen on screen. The modal's error toast surfaces the reason.
      setRevealCard(null)
    }
    setBusy(false)
  }

  if (!state && err) return (
    <p style={{ fontSize: '0.8rem', color: 'var(--red)' }}>Shuffle: {err}</p>
  )
  if (!state) return (
    <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Loading Shuffle…</p>
  )

  const needsEtiquette = !state.etiquette_acknowledged_by?.includes(viewerPlayerId)

  return (
    <div>
      <EtiquetteModal open={needsEtiquette} onAck={ackEtiquette} />

      <ActiveEffects
        activeSpades={state.active_spades || []}
        crossHoleBonuses={state.cross_hole_bonuses || []}
        currentHole={viewerCurrentHole || 1}
      />

      {/* Draw modal — 2x2 popup + reveal animation. Stays mounted while
          EITHER offer.picks_remaining>0 (the "you still have cards to
          draw" state) OR revealCard is set (the "flip + read" animation
          is in progress and unmounting now would kill it mid-cycle).
          When onAutoClose fires (3s dwell after flip completes), we
          clear revealCard AND — if picks_remaining also dropped to 0 —
          hide the modal until next hole. In Double Down or after a
          ♦5 compound, picks_remaining stays >0 and the modal re-enters
          'offered' state for the next pick. */}
      {(revealCard || (offer && offer.picks_remaining > 0))
        && !needsEtiquette && !pickerHidden && (
        <DrawModal
          offer={offer?.offer || []}
          picksRemaining={offer?.picks_remaining || 0}
          revealCard={revealCard}
          onPick={onPick}
          onAutoClose={() => {
            setRevealCard(null)
            if (!offer || offer.picks_remaining <= 0) setPickerHidden(true)
          }}
          onDismiss={() => setPickerHidden(true)}
          busy={busy}
        />
      )}

      {/* Reopen affordance when the picker was dismissed but there's
          still a pick outstanding. */}
      {offer && offer.picks_remaining > 0 && pickerHidden && (
        <button
          onClick={() => setPickerHidden(false)}
          style={{
            display: 'block', width: '100%', marginBottom: 8,
            padding: '10px 12px', borderRadius: 6,
            background: 'var(--gold)', color: 'var(--green-deep)',
            border: 'none', fontWeight: 700, fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          Draw Shuffle card{offer.picks_remaining > 1 ? 's' : ''} ({offer.picks_remaining} left)
        </button>
      )}

      {/* In-play card banner — how "current" is derived depends on
          the variant.
            inline (scorecard) → the draw(s) tied to viewerCurrentHole
            full (/shuffle)    → the SINGLE most recent draw across all
                                 holes; the /shuffle page is deliberately
                                 hole-agnostic per Jacob 2026-07-14
                                 ("last card equals In Play and the rest
                                 are history").
      */}
      {variant === 'inline' && offer && offer.already_drawn?.length > 0 && (
        <>
          {offer.already_drawn.map(card => {
            const draw = (state.draws || []).find(d => d.card === card && d.hole_number === viewerCurrentHole)
            return (
              <EffectBanner
                key={`b-${card}`}
                card={card}
                effect={draw?.effect}
                holeNumber={viewerCurrentHole}
              />
            )
          })}
        </>
      )}
      {variant === 'full' && (state.draws || []).length > 0 && (() => {
        const latest = [...(state.draws || [])].sort(
          (a, b) => (b.hole_number - a.hole_number) || 0
        )[0]
        if (!latest) return null
        return (
          <div style={{
            marginBottom: 8, padding: '4px 8px',
            borderRadius: 4,
            background: 'rgba(212,185,122,0.12)',
            border: '1px solid var(--gold)',
            fontSize: '0.7rem', color: 'var(--gold)',
            fontWeight: 700, letterSpacing: '0.05em',
            textTransform: 'uppercase', textAlign: 'center',
          }}>In Play</div>
        )
      })()}
      {variant === 'full' && (state.draws || []).length > 0 && (() => {
        const latest = [...(state.draws || [])].sort(
          (a, b) => (b.hole_number - a.hole_number) || 0
        )[0]
        if (!latest) return null
        return (
          <EffectBanner
            card={latest.card}
            effect={latest.effect}
            holeNumber={latest.hole_number}
          />
        )
      })()}

      {/* Deck count + history toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>
          Deck {state.deck_remaining}/52 · {state.mode.replace('_', ' ')}
        </span>
        <button
          onClick={() => setShowHistory(x => !x)}
          style={{
            padding: '3px 10px', fontSize: '0.7rem',
            background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold)',
            borderRadius: 4, cursor: 'pointer',
          }}
        >
          {showHistory
            ? 'Hide history'
            : `History (${
                variant === 'full'
                  ? Math.max(0, (state.draws || []).length - 1)
                  : (state.draws || []).filter(d => d.hole_number < (viewerCurrentHole || 1)).length
              })`}
        </button>
      </div>

      {showHistory && (
        <div style={{ marginTop: 8, padding: 8, background: 'var(--green-dark)', borderRadius: 6 }}>
          <HistoryView draws={state.draws || []} currentHole={viewerCurrentHole || 1} variant={variant} />
        </div>
      )}

      {err && <p style={{ fontSize: '0.7rem', color: 'var(--red)', marginTop: 6 }}>{err}</p>}
    </div>
  )
}
